import { useState, useEffect, useCallback } from "react";
import {
  Mail, CheckCircle2, XCircle, AlertCircle, RefreshCw, ShieldCheck,
  Loader2, Server, Globe, ArrowRight, Info, ChevronDown, ChevronUp,
  ClipboardCheck, Power
} from "lucide-react";
import { emailChannelApi } from "@/services/api";
import { toast } from "sonner";

const LIFECYCLE_STEPS = ["CONFIGURE", "VALIDATE", "APPROVE", "ACTIVE"];

export default function EmailChannelSetup() {
  const [health, setHealth] = useState(null);
  const [config, setConfig] = useState(null);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [showChecks, setShowChecks] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([emailChannelApi.getHealth(), emailChannelApi.getChannelConfig()])
      .then(([h, c]) => {
        setHealth(h.data);
        setConfig(c.data.config);
        setValidation(c.data.validation);
      })
      .catch(() => toast.error("Failed to load email channel status"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleActivate() {
    setActivating(true);
    try {
      const res = await emailChannelApi.activateChannel();
      if (res.data.activated) {
        toast.success("EMAIL channel activated successfully");
        load();
      } else {
        toast.error(`Activation blocked: ${res.data.blockers?.join(", ") || "checks not passed"}`);
      }
    } catch {
      toast.error("Activation failed");
    } finally {
      setActivating(false);
    }
  }

  async function recordTest(testType, passed) {
    try {
      await emailChannelApi.recordTestResult({ test_type: testType, passed, notes: "" });
      toast.success(`${testType} test recorded as ${passed ? "PASSED" : "FAILED"}`);
      load();
    } catch {
      toast.error("Failed to record test result");
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0176D3]" />
      </div>
    );
  }

  const provider = health?.email_provider || "mock";
  const status = health?.email_status || "CONFIGURED_NOT_ENABLED";
  const verificationMode = health?.verification_mode || "TEST";
  const currentStep = validation?.current_status || "NOT_CONFIGURED";
  const stepIdx = LIFECYCLE_STEPS.indexOf(currentStep);
  const isActive = currentStep === "ACTIVE";
  const isMock = provider === "mock";
  const checks = validation?.checks || {};
  const blockers = validation?.activation_blockers || [];

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-5 h-5 text-[#0176D3]" />
            <h1 className="text-2xl font-bold text-[#181818]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Email Channel Setup
            </h1>
          </div>
          <p className="text-[#706E6B] text-sm">
            Stage 01.1 — Production email enablement via SendGrid
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[#706E6B] hover:text-[#181818] border border-[#C9C9C9] rounded-lg text-xs transition-colors"
          data-testid="email-channel-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* Provider + Status */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatusCard
          icon={<Server className="w-4 h-4" />}
          label="Active Provider"
          value={provider.toUpperCase()}
          badge={isMock ? "warn" : "ok"}
          badgeText={isMock ? "DEV ONLY" : "PRODUCTION"}
          testId="email-provider-card"
        />
        <StatusCard
          icon={<Mail className="w-4 h-4" />}
          label="Channel Status"
          value={currentStep === "NOT_CONFIGURED" ? "NOT CONFIGURED" : currentStep}
          badge={isActive ? "ok" : "neutral"}
          badgeText={isActive ? "ACTIVE" : currentStep === "NOT_CONFIGURED" ? "SETUP NEEDED" : "IN PROGRESS"}
          testId="email-status-card"
        />
        <StatusCard
          icon={<ShieldCheck className="w-4 h-4" />}
          label="Verification Mode"
          value={verificationMode}
          badge={verificationMode === "ENFORCED" ? "ok" : "warn"}
          badgeText={verificationMode === "ENFORCED" ? "ENFORCED" : "TEST MODE"}
          testId="email-verification-card"
        />
      </div>

      {/* Lifecycle progress */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-5">
        <h2 className="text-sm font-semibold text-[#444444] mb-4">Channel Lifecycle</h2>
        <div className="flex items-center gap-0" data-testid="email-lifecycle">
          {LIFECYCLE_STEPS.map((step, i) => {
            const done = i < stepIdx || isActive;
            const active = step === currentStep;
            return (
              <div key={step} className="flex items-center flex-1">
                <div className="flex flex-col items-center gap-1 flex-1">
                  <div
                    className={[
                      "w-8 h-8 rounded-full flex items-center justify-center border-2 text-xs font-bold transition-all",
                      done || active
                        ? "border-[#0176D3] bg-[#EEF4FF] text-[#0176D3]"
                        : "border-[#C9C9C9] bg-transparent text-[#706E6B]"
                    ].join(" ")}
                  >
                    {done && !active ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`text-[10px] font-medium uppercase tracking-wider ${active || done ? "text-[#0176D3]" : "text-[#706E6B]"}`}>
                    {step}
                  </span>
                </div>
                {i < LIFECYCLE_STEPS.length - 1 && (
                  <div className={`h-px flex-1 mx-1 ${done ? "bg-[#0176D3]/40" : "bg-[#FAFAFA]"}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mock adapter warning */}
      {isMock && (
        <div className="flex items-start gap-3 p-4 bg-[#FFF4E5] border border-[#FE9339]/30 rounded-xl" data-testid="mock-warning-banner">
          <AlertCircle className="w-4 h-4 text-[#8C4B00] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-[#8C4B00]">MockEmailAdapter is active</p>
            <p className="text-xs text-amber-500 mt-0.5">
              EMAIL channel cannot reach ACTIVE state while using MockEmailAdapter.
              Set <code className="font-mono">EMAIL_PROVIDER=sendgrid</code> in backend <code>.env</code> to switch to production.
            </p>
          </div>
        </div>
      )}

      {/* Validation checklist */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden">
        <button
          className="w-full flex items-center justify-between p-4 text-left hover:bg-[#FAFAFA] transition-colors"
          onClick={() => setShowChecks(v => !v)}
          data-testid="email-validation-toggle"
        >
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-[#706E6B]" />
            <span className="text-sm font-semibold text-[#444444]">Activation Checklist</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              blockers.length === 0 ? "bg-[#EAF5EE] text-[#2E844A]" : "bg-[#FCEBEC] text-[#BA0517]"
            }`}>
              {blockers.length === 0 ? "ALL PASSED" : `${blockers.length} blocking`}
            </span>
          </div>
          {showChecks ? <ChevronUp className="w-4 h-4 text-[#706E6B]" /> : <ChevronDown className="w-4 h-4 text-[#706E6B]" />}
        </button>

        {showChecks && (
          <div className="border-t border-[#E5E5E5] divide-y divide-slate-800/50">
            {Object.entries(checks).map(([key, passed]) => (
              <div key={key} className="flex items-center gap-3 px-4 py-2.5" data-testid={`check-${key}`}>
                {passed
                  ? <CheckCircle2 className="w-4 h-4 text-[#2E844A] flex-shrink-0" />
                  : <XCircle className="w-4 h-4 text-[#BA0517] flex-shrink-0" />
                }
                <span className={`text-xs font-mono ${passed ? "text-[#706E6B]" : "text-[#BA0517]"}`}>
                  {key.replace(/_/g, " ")}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Test record buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TestPanel
          title="Outbound Send Test"
          description="Record result after sending a test email to a verified address"
          passed={validation?.checks?.outbound_test_passed}
          onPass={() => recordTest("outbound", true)}
          onFail={() => recordTest("outbound", false)}
          testIdPrefix="outbound-test"
        />
        <TestPanel
          title="Inbound Parse Test"
          description="Record result after receiving a reply that triggers inbound webhook"
          passed={validation?.checks?.inbound_test_passed}
          onPass={() => recordTest("inbound", true)}
          onFail={() => recordTest("inbound", false)}
          testIdPrefix="inbound-test"
        />
      </div>

      {/* Activation */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[#444444] flex items-center gap-2">
              <Power className="w-4 h-4 text-[#0176D3]" />
              Activate EMAIL Channel
            </h3>
            <p className="text-xs text-[#706E6B] mt-0.5">
              All checklist items must pass before activation is permitted.
            </p>
          </div>
          <button
            data-testid="email-activate-btn"
            onClick={handleActivate}
            disabled={activating || blockers.length > 0}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              backgroundColor: blockers.length === 0 ? "#0176D3" : "#1e293b",
              color: blockers.length === 0 ? "#0f172a" : "#64748b",
              border: blockers.length === 0 ? "none" : "1px solid #444444",
            }}
          >
            {activating && <Loader2 className="w-4 h-4 animate-spin" />}
            {isActive ? "Already Active" : "Activate Channel"}
          </button>
        </div>
        {blockers.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
            <p className="text-xs text-[#706E6B] mb-1">Blocking checks:</p>
            <div className="flex flex-wrap gap-1.5">
              {blockers.map(b => (
                <span key={b} className="text-xs px-2 py-0.5 bg-[#FCEBEC] text-[#BA0517] rounded font-mono">
                  {b.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Env var reference */}
      <EnvVarReference verificationMode={verificationMode} />
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusCard({ icon, label, value, badge, badgeText, testId }) {
  const badgeStyle = {
    ok: "bg-[#EAF5EE] text-[#2E844A]",
    warn: "bg-[#FFF4E5] text-[#8C4B00]",
    neutral: "bg-[#FAFAFA] text-[#706E6B]",
  };
  return (
    <div
      className="bg-white border border-[#E5E5E5] rounded-xl p-4"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-[#706E6B] mb-2">
        {icon}
        <span className="text-xs uppercase tracking-wide">{label}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-[#181818]">{value}</span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${badgeStyle[badge]}`}>
          {badgeText}
        </span>
      </div>
    </div>
  );
}

function TestPanel({ title, description, passed, onPass, onFail, testIdPrefix }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-4">
      <div className="flex items-start gap-2 mb-3">
        {passed === true
          ? <CheckCircle2 className="w-4 h-4 text-[#2E844A] flex-shrink-0 mt-0.5" />
          : passed === false
          ? <XCircle className="w-4 h-4 text-[#BA0517] flex-shrink-0 mt-0.5" />
          : <Info className="w-4 h-4 text-[#706E6B] flex-shrink-0 mt-0.5" />
        }
        <div>
          <p className="text-sm font-semibold text-[#444444]">{title}</p>
          <p className="text-xs text-[#706E6B] mt-0.5">{description}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          data-testid={`${testIdPrefix}-pass`}
          onClick={onPass}
          className="flex-1 py-1.5 rounded-lg text-xs font-medium bg-[#EAF5EE] text-[#2E844A] hover:bg-[#EAF5EE] transition-colors border border-[#2E844A]/20"
        >
          Mark Passed
        </button>
        <button
          data-testid={`${testIdPrefix}-fail`}
          onClick={onFail}
          className="flex-1 py-1.5 rounded-lg text-xs font-medium bg-[#FCEBEC] text-[#BA0517] hover:bg-[#FCEBEC] transition-colors border border-[#BA0517]/20"
        >
          Mark Failed
        </button>
      </div>
    </div>
  );
}

function EnvVarReference({ verificationMode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between p-4 text-left hover:bg-[#FAFAFA]"
        onClick={() => setOpen(v => !v)}
      >
        <span className="text-xs font-semibold text-[#706E6B] uppercase tracking-wide">
          Required Environment Variables
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-[#706E6B]" /> : <ChevronDown className="w-4 h-4 text-[#706E6B]" />}
      </button>
      {open && (
        <div className="border-t border-[#E5E5E5] p-4 space-y-2">
          {[
            ["EMAIL_PROVIDER", "sendgrid", "Switch from mock to production"],
            ["SENDGRID_API_KEY", "SG.xxx...", "Least-privilege key (Mail Send)"],
            ["SENDGRID_FROM_ADDRESS", "events@yourdomain.com", "Verified sender"],
            ["SENDGRID_FROM_NAME", "Prashasta Events", "Display name"],
            ["SENDGRID_INBOUND_DOMAIN", "reply.yourdomain.com", "Inbound Parse domain"],
            ["SENDGRID_INBOUND_WEBHOOK_SECRET", "...", "Shared secret for inbound parse"],
            ["SENDGRID_EVENT_WEBHOOK_PUBLIC_KEY", "MFkw...", "ECDSA public key for event webhooks"],
            ["EMAIL_CHANNEL_VERIFICATION_MODE", "ENFORCED", `Currently: ${verificationMode}`],
          ].map(([key, example, note]) => (
            <div key={key} className="flex items-start gap-2">
              <code className="text-[10px] font-mono text-sky-400 min-w-[280px]">{key}</code>
              <span className="text-[10px] text-[#706E6B]">{note}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
